import axios from "axios";
import React, { useState, useRef, useEffect } from "react";

const ChatBot = ({ onClose }) => {
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Hi 👋 Welcome to Strikes & Shots! Play. Train. Celebrate. All under one roof. How can I help you today?",
      timestamp: new Date(),
    },
  ]);

  const [input, setInput] = useState("");
  const [step, setStep] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationFlow, setConversationFlow] = useState("initial");
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    message: "",
  });
  const [currentDetail, setCurrentDetail] = useState("");
  const [userInquiry, setUserInquiry] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("");
  const messagesEndRef = useRef(null);
  const [contactData, setContactData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch contact data
        const contactResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/contactinfo/get-contact`
        );
        if (contactResponse.data?.success && contactResponse.data?.data) {
          setContactData(contactResponse.data.data);
        }

        // Fetch events
        const eventsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/chatbotevent/get-all`
        );
        if (eventsResponse.data?.success && eventsResponse.data?.data) {
          setEvents(eventsResponse.data.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const simulateTyping = (responseMessages, callback) => {
    setIsTyping(true);
    let currentIndex = 0;

    const typeNextMessage = () => {
      if (currentIndex < responseMessages.length) {
        setMessages((prev) => [...prev, responseMessages[currentIndex]]);
        currentIndex++;
        setTimeout(typeNextMessage, 800);
      } else {
        setIsTyping(false);
        if (callback) callback();
      }
    };

    setTimeout(typeNextMessage, 600);
  };

  const handleContactInfo = () => {
    setIsTyping(true);
    setTimeout(() => {
      const botResponses = [
        {
          from: "bot",
          text: "Here are our contact details. We'd love to hear from you! 📞",
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: `📍 **Address:**\n${
            contactData?.address ||
            "4285 Brogdon Exchange NW, Suwanee, GA 30024"
          }`,
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: `📞 **Phone:**\n${contactData?.no1 || "(678) 287-1100"}\n${
            contactData?.no2 || "(678) 714-7454"
          }`,
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: `💬 **WhatsApp:**\n${
            contactData?.whatsappNo || "(404) 200-2954"
          }`,
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: `✉️ **Email:**\n${
            contactData?.email || "info@strikeandshots.com"
          }`,
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: "🕒 **Business Hours:**\n• Monday-Thursday: 10 AM - 11 PM\n• Friday-Saturday: 10 AM - 12 AM\n• Sunday: 11 AM - 10 PM",
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: "Feel free to reach out to us for any questions, bookings, or just to say hello! We're always happy to help. 😊",
          timestamp: new Date(),
        },
      ];
      simulateTyping(botResponses, () => {
        setStep(1);
        setConversationFlow("free_chat");
      });
    }, 800);
  };

  const startEventBooking = (eventTitle, eventId) => {
    setSelectedEventId(eventId);
    setUserInquiry(eventTitle);
    setIsTyping(true);
    setTimeout(() => {
      const botResponses = [
        {
          from: "bot",
          text: `Great choice! Let's book a inquiry on "${eventTitle}" for you. I'll need a few details to complete your booking. Please enter your name.`,
          timestamp: new Date(),
        },
      ];
      simulateTyping(botResponses, () => {
        setConversationFlow("collecting_details");
        setCurrentDetail("name");
      });
    }, 800);
  };

  const handleEventBookingDetails = (text) => {
    const updatedDetails = { ...userDetails };

    switch (currentDetail) {
      case "name":
        updatedDetails.name = text;
        setUserDetails(updatedDetails);
        setCurrentDetail("phone");
        setIsTyping(true);
        setTimeout(() => {
          const botResponses = [
            {
              from: "bot",
              text: "Thanks! What's your phone number?",
              timestamp: new Date(),
            },
          ];
          simulateTyping(botResponses);
        }, 800);
        break;

      case "phone":
        const phoneRegex = /[\d\s\-\(\)\+]{10,}/;
        if (!phoneRegex.test(text.replace(/\D/g, ""))) {
          setMessages((prev) => [
            ...prev,
            {
              from: "bot",
              text: "Please enter a valid phone number (at least 10 digits):",
              timestamp: new Date(),
            },
          ]);
          return;
        }
        updatedDetails.phone = text;
        setUserDetails(updatedDetails);
        setCurrentDetail("email");
        setIsTyping(true);
        setTimeout(() => {
          const botResponses = [
            {
              from: "bot",
              text: "Perfect! What's your email address? (Optional - type 'skip' to skip)",
              timestamp: new Date(),
            },
          ];
          simulateTyping(botResponses);
        }, 800);
        break;

      case "email":
        if (text.toLowerCase() === "skip") {
          updatedDetails.email = "";
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(text)) {
            setMessages((prev) => [
              ...prev,
              {
                from: "bot",
                text: "Please enter a valid email address or type 'skip' to skip:",
                timestamp: new Date(),
              },
            ]);
            return;
          }
          updatedDetails.email = text;
        }
        setUserDetails(updatedDetails);
        setCurrentDetail("address");
        setIsTyping(true);
        setTimeout(() => {
          const botResponses = [
            {
              from: "bot",
              text: "Great! What's your address? (Optional - type 'skip' to skip)",
              timestamp: new Date(),
            },
          ];
          simulateTyping(botResponses);
        }, 800);
        break;

      case "address":
        if (text.toLowerCase() !== "skip") {
          updatedDetails.address = text;
        } else {
          updatedDetails.address = "";
        }
        setUserDetails(updatedDetails);
        setCurrentDetail("message");
        setIsTyping(true);
        setTimeout(() => {
          const botResponses = [
            {
              from: "bot",
              text: "Almost done! Any message or special requests? (Optional - type 'skip' to skip)",
              timestamp: new Date(),
            },
          ];
          simulateTyping(botResponses);
        }, 800);
        break;

      case "message":
        if (text.toLowerCase() !== "skip") {
          updatedDetails.message = text;
        }
        setUserDetails(updatedDetails);
        completeEventBooking(updatedDetails);
        break;

      default:
        break;
    }
  };

  const completeEventBooking = async (details) => {
    setIsTyping(true);

    try {
      // Send booking data to API
      const bookingData = {
        eventId: selectedEventId,
        name: details.name,
        phone: details.phone,
        email: details.email || undefined,
        address: details.address || undefined,
        message: details.message || undefined,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/chatbotevent/book`,
        bookingData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setTimeout(() => {
        const botResponses = [
          {
            from: "bot",
            text: "🎉 Your Inquiry has been submitted successfully!",
            timestamp: new Date(),
          },
          {
            from: "bot",
            text: `📋 **Booking Summary:**\n🎪 **Event:** ${userInquiry}\n👤 **Name:** ${
              details.name
            }\n📞 **Phone:** ${details.phone}${
              details.email ? `\n📧 **Email:** ${details.email}` : ""
            }${details.address ? `\n📍 **Address:** ${details.address}` : ""}${
              details.message ? `\n💬 **Message:** ${details.message}` : ""
            }`,
            timestamp: new Date(),
          },
          {
            from: "bot",
            text: "Our team will contact you shortly to confirm the details. We're excited to celebrate with you! 🎊",
            timestamp: new Date(),
          },
          {
            from: "bot",
            text: "For immediate assistance:\n📍 Visit us: 4285 Brogdon Exchange NW, Suwanee, GA 30024\n📞 Call: (678) 714-7454\n✉️ Email: info@sportsandstrike.com",
            timestamp: new Date(),
          },
        ];
        simulateTyping(botResponses, () => {
          setConversationFlow("free_chat");
          setStep(1);

          // Here you can also send the user details to your backend
          console.log("User Inquiry Details:", {
            inquiryType: userInquiry,
            userDetails: details,
            timestamp: new Date().toISOString(),
          });

          // Reset user details for next conversation
          setUserDetails({
            name: "",
            email: "",
            phone: "",
            address: "",
            message: "",
          });
          setUserInquiry("");
          setSelectedEventId("");
        });
      }, 800);
    } catch (error) {
      console.error("Error booking event:", error);
      setTimeout(() => {
        const errorResponses = [
          {
            from: "bot",
            text: "Oops! There was an issue processing your booking. Please try again or contact us directly.",
            timestamp: new Date(),
          },
          {
            from: "bot",
            text: "📞 Call: (678) 714-7454\n✉️ Email: info@strikeandshots.com",
            timestamp: new Date(),
          },
        ];
        simulateTyping(errorResponses, () => {
          setConversationFlow("free_chat");
          setStep(1);
        });
      }, 800);
    }
  };

  const handleFreeMessage = (userMessage) => {
    setIsTyping(true);
    const userMessageLower = userMessage.toLowerCase();

    setTimeout(() => {
      let botResponse = "";

      if (
        userMessageLower.includes("hour") ||
        userMessageLower.includes("open") ||
        userMessageLower.includes("time")
      ) {
        botResponse =
          "We're open:\n• Monday-Thursday: 10 AM - 11 PM\n• Friday-Saturday: 10 AM - 12 AM\n• Sunday: 11 AM - 10 PM\n\nWeekend hours may vary during special events! 🕒";
      } else if (
        userMessageLower.includes("price") ||
        userMessageLower.includes("cost") ||
        userMessageLower.includes("fee")
      ) {
        botResponse =
          "Our pricing varies based on activities and duration:\n• Bowling: $25-40 per hour per lane\n• Training sessions: $50-80 per hour\n• Event packages: Starting at $299\n\nFor exact pricing, our team can provide a customized quote based on your needs! 💰";
      } else if (
        userMessageLower.includes("parking") ||
        userMessageLower.includes("car")
      ) {
        botResponse =
          "We have ample free parking available! Our facility has a large parking lot with dedicated spaces near the entrance for easy access. 🅿️";
      } else if (
        userMessageLower.includes("food") ||
        userMessageLower.includes("drink") ||
        userMessageLower.includes("menu")
      ) {
        botResponse =
          "Yes! We have a full-service restaurant and bar serving:\n• Fresh pizzas, burgers, and snacks\n• Craft beers and cocktails\n• Non-alcoholic beverages and coffee\n\nYou can enjoy food and drinks while you play! 🍕🥤";
      } else if (
        userMessageLower.includes("child") ||
        userMessageLower.includes("kid") ||
        userMessageLower.includes("family")
      ) {
        botResponse =
          "We're family-friendly! We offer:\n• Kids' bowling packages\n• Family discount rates\n• Child-safe equipment\n• Special family events on weekends\n\nPerfect for birthday parties and family outings! 👨‍👩‍👧‍👦";
      } else if (
        userMessageLower.includes("birthday") ||
        userMessageLower.includes("party") ||
        userMessageLower.includes("event")
      ) {
        botResponse =
          "Great choice for celebrations! We offer:\n• Custom party packages\n• Dedicated event coordinator\n• Food and beverage options\n• Decorations and setup\n• Special rates for groups\n\nWould you like me to connect you with our events team? 🎉";
      } else if (
        userMessageLower.includes("thank") ||
        userMessageLower.includes("thanks")
      ) {
        botResponse =
          "You're welcome! 😊 Is there anything else I can help you with today?";
      } else if (
        userMessageLower.includes("hello") ||
        userMessageLower.includes("hi") ||
        userMessageLower.includes("hey")
      ) {
        botResponse = "Hello again! 👋 How can I assist you further?";
      } else if (
        userMessageLower.includes("bye") ||
        userMessageLower.includes("goodbye")
      ) {
        botResponse =
          "Thanks for chatting! Have a great day and hope to see you soon at Strike & Shots! 🎳✨";
      } else if (userMessageLower.includes("name")) {
        botResponse =
          "I'm your Strike & Shots assistant! I'm here to help you with information about our facilities, bookings, events, and anything else you need. 😊";
      } else {
        botResponse =
          "Thanks for your message! I'd be happy to help you with that. Our team at Strike & Shots can assist with all your gaming and event needs. Could you tell me a bit more about what you're looking for?";
      }

      const botResponses = [
        { from: "bot", text: botResponse, timestamp: new Date() },
      ];

      if (
        !userMessageLower.includes("thank") &&
        !userMessageLower.includes("hello") &&
        !userMessageLower.includes("hi") &&
        !userMessageLower.includes("bye") &&
        !userMessageLower.includes("name")
      ) {
        botResponses.push({
          from: "bot",
          text: `For immediate assistance, you can also:\n📍 Visit us: ${
            contactData?.address ||
            "4285 Brogdon Exchange NW, Suwanee, GA 30024"
          }\n📞 Call: ${contactData?.no1 || "(678) 287-1100"} or ${
            contactData?.no2 || "(678) 714-7454"
          }\n💬 WhatsApp: ${
            contactData?.whatsappNo || "(404) 200-2954"
          }\n✉️ Email: ${contactData?.email || "info@strikeandshots.com"}`,
          timestamp: new Date(),
        });
      }

      simulateTyping(botResponses, () => {
        setConversationFlow("free_chat");
        setStep(1);
      });
    }, 1000);
  };

  const handleSend = (text) => {
    if (!text.trim()) return;

    const userMessage = {
      from: "user",
      text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    if (conversationFlow === "collecting_details") {
      handleEventBookingDetails(text);
    } else if (text === "Contact Info") {
      handleContactInfo();
    } else if (step === 0 && conversationFlow === "initial") {
      const selectedEvent = events.find((event) => event.title === text);
      if (selectedEvent) {
        startEventBooking(selectedEvent.title, selectedEvent._id);
      } else {
        handleFreeMessage(text);
      }
    } else {
      handleFreeMessage(text);
    }

    setInput("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const resetChat = () => {
    setMessages([
      {
        from: "bot",
        text: "Hi 👋 Welcome to Strike & Shots! Play. Train. Celebrate. All under one roof. How can I help you today?",
        timestamp: new Date(),
      },
    ]);
    setStep(0);
    setConversationFlow("initial");
    setInput("");
    setUserDetails({
      name: "",
      email: "",
      phone: "",
      address: "",
      message: "",
    });
    setUserInquiry("");
    setCurrentDetail("");
    setSelectedEventId("");
  };

  const getInputPlaceholder = () => {
    if (conversationFlow === "collecting_details") {
      switch (currentDetail) {
        case "name":
          return "Enter your full name...";
        case "email":
          return "Enter your email or type 'skip'...";
        case "phone":
          return "Enter your phone number...";
        case "address":
          return "Enter your address or type 'skip'...";
        case "message":
          return "Enter your message or type 'skip'...";
        default:
          return "Type your response...";
      }
    }
    return "Type your message or question...";
  };

  return (
    <div className="fixed bottom-20 right-6 w-80 h-[28rem] bg-white shadow-2xl rounded-xl border border-gray-200 flex flex-col z-50 font-sans">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-t-xl flex justify-between items-center shadow-md">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="font-semibold text-sm">
            {contactData?.chatbotName || "Strike & Shots Assistant"}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={resetChat}
            className="text-white hover:bg-white/20 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 text-sm font-light"
            title="Start new conversation"
          >
            ↺
          </button>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 text-lg font-light"
          >
            ×
          </button>
        </div>
      </div>

      <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
        <div className="space-y-3">
          {messages.map((msg, idx) => {
            // Skip rendering if message text is empty or undefined
            if (!msg?.text || msg.text.trim() === "") return null;

            return (
              <div
                key={idx}
                className={`flex flex-col ${
                  msg?.from === "bot" ? "items-start" : "items-end"
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    msg?.from === "bot"
                      ? "bg-white border border-gray-200 text-gray-800 rounded-tl-none shadow-sm"
                      : "bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-tr-none shadow"
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {msg?.text}
                  </p>
                  <span
                    className={`text-xs mt-1 block ${
                      msg?.from === "bot" ? "text-gray-500" : "text-blue-100"
                    }`}
                  >
                    {formatTime(msg?.timestamp)}
                  </span>
                </div>
              </div>
            );
          })}

          {isTyping && (
            <div className="flex items-start space-x-2">
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {step === 0 && !isTyping && conversationFlow === "initial" && (
            <div className="space-y-2 mt-2">
              <p className="text-xs text-gray-500 font-medium mb-2">
                Choose an option or type your question directly:
              </p>
              {loading ? (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">Loading events...</p>
                </div>
              ) : (
                <>
                  {events.map((event) => (
                    <button
                      key={event._id}
                      onClick={() => handleSend(event.title)}
                      className="block w-full text-left bg-white border border-gray-200 px-4 py-3 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 text-sm font-medium text-gray-700 shadow-sm hover:shadow"
                    >
                      {event.title}
                    </button>
                  ))}
                  <button
                    onClick={() => handleSend("Contact Info")}
                    className="block w-full text-left bg-white border border-gray-200 px-4 py-3 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 text-sm font-medium text-gray-700 shadow-sm hover:shadow"
                  >
                    Contact Info
                  </button>
                </>
              )}
            </div>
          )}

          {(conversationFlow === "other_inquiry" ||
            conversationFlow === "free_chat") &&
            !isTyping && (
              <div className="text-center mt-2">
                <p className="text-xs text-gray-500">
                  Feel free to ask more questions! 💬
                </p>
              </div>
            )}
        </div>
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t border-gray-200 bg-white rounded-b-xl">
        <div className="flex space-x-2">
          <input
            type={
              currentDetail === "email"
                ? "email"
                : currentDetail === "phone"
                ? "tel"
                : "text"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={getInputPlaceholder()}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            disabled={isTyping}
          />
          <button
            onClick={() => handleSend(input)}
            disabled={!input.trim() || isTyping}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm font-medium text-sm"
          >
            Send
          </button>
        </div>
        <p className="text-xs text-gray-400 text-center mt-2">
          {conversationFlow === "initial"
            ? "Ask me anything about Strike & Shots!"
            : "We're here to help with all your questions!"}
        </p>
      </div>
    </div>
  );
};

export default ChatBot;
